/**
 * IMPORTANT: If we adjust this file we have to ensure that we also update it in our frontend & backend.
 *
 * We should later move this to a separate package and share it with the frontend.
 */

export type ExceptionBase<T = undefined> = T extends undefined
  ? ExceptionWithoutData
  : ExceptionWithoutData & { data: T };

export interface ExceptionWithoutData {
  /**
   * The http status code of the exception.
   */
  statusCode: number;

  /**
   * The name of the exception.
   */
  name: ExceptionName;

  /**
   * A short description of the error.
   */
  description: string;

  /**
   * The timestamp when the exception has occurred
   */
  timestamp: string;

  /**
   * The request url which has caused this exception.
   */
  path: string;

  /**
   * The inner exception with more details about the exception.
   */
  innerException?: InnerException;
}

/**
 * This is the actual exception which was thrown in our backend.
 * For example the db throws an error or an external service is not reachable.
 *
 * The reason why we are doing this is to not confuse us developer in the frontend (e.g the backend calls an invalid endpoint in Quicksale)
 * and we receive an HTTP 404.
 *
 * By using an inner exception we can return an HTTP 500 for the outer and an HTTP 404 for the inner exception.
 * By returning the HTTP 500 we are acknowledging that it's in our responsibility and that the user did not do anything wrong.
 *
 * Note: We only expose properties which can be considered safe (no raw exceptions & stacktraces)
 */
interface InnerException {
  /**
   * The status code of the inner exception.
   */
  statusCode: number;

  /**
   * The name of the inner exception.
   *
   * For now we are not using an enum as this should never be returned on production and thus displayed to the user.
   */
  name: string;

  /**
   * A short description of the inner exception.
   */
  description: string;
}

export enum ExceptionName {
  // General
  BAD_REQUEST = "Bad Request",
  NOT_FOUND = "Not Found",
  GENERAL_ERROR = "General Error",
  INVALID_URL = "Invalid Url",

  // Quote
  UNKNOWN_TERM_LIFE_INSURANCE_DOCUMENT_TYPE = "Unknown Term Life Insurance Document Type",
  INVALID_POSTCODE = "Provided Postcode Not Valid",
  QUOTE_NOT_FOUND = "Quote Not Found",
  INVALID_TARIFF_FOR_QUOTE = "Invalid Tariff For Quote",

  // Application
  APPLICATION_NOT_FOUND = "Application Not Found",
  CREATE_APPLICATION_FAILED = "Create Application Failed",
  NOT_ALLOWED_TO_CREATE_MULTIPLE_APPLICATIONS_FROM_QUOTE = "Not Allowed To Create Multiple Applications From A Single Quote",
  APPLICATION_DOCUMENT_NOT_FOUND = "Application Document Not Found",
  ACCESS_TO_APPLICATION_HAS_EXPIRED = "Access To Application Has Expired",
  UNKNOWN_APPLICATION_DOCUMENT_TYPE = "Unknown Application Document Type",
  APPLICATION_PDF_QR_CODE_URL_MISSING_FOR_DIGITAL_APPLICATION = "Application Pdf QR Code Url Is Missing For Digital Application",
}

/**
 * An aggregated exception of all the validation errors from our dto.
 */
export interface ValidationException extends ExceptionBase<ValidationData> {
  name: ExceptionName.BAD_REQUEST;
}

export interface ValidationData {
  validationErrors: string[];
}

/**
 * An exception which is thrown when some of the required data is missing when creating an 'application' (immutable) from a 'quote' (mutable).
 */
export interface CreateApplicationFailedException
  extends ExceptionBase<CreateApplicationFailedData> {
  name: ExceptionName.CREATE_APPLICATION_FAILED;
}

export interface CreateApplicationFailedData {
  /**
   * Required properties which are missing on the referenced quote.
   */
  properties: string[];
}

/**
 * Generic error which among others is used on our production environment as we do not want to expose any internal details.
 */
export interface GeneralErrorException extends ExceptionBase {
  name: ExceptionName.GENERAL_ERROR;
}

/**
 * Exception thrown when no matching route can be found.
 */
export interface NotFoundException extends ExceptionBase {
  name: ExceptionName.NOT_FOUND;
}

/**
 * Exception thrown when the api consumer has provided a url which has not been whitelisted.
 */
export interface InvalidUrlException extends ExceptionBase {
  name: ExceptionName.INVALID_URL;
}

/**
 * Exception thrown when the document type is not known.
 */
export interface UnknownTermLifeInsuranceDocumentTypeException
  extends ExceptionBase {
  name: ExceptionName.UNKNOWN_TERM_LIFE_INSURANCE_DOCUMENT_TYPE;
}

/**
 * Exception thrown when a quote can not be found.
 */
export interface QuoteNotFoundException extends ExceptionBase {
  name: ExceptionName.QUOTE_NOT_FOUND;
}

export interface InvalidTariffForQuoteException extends ExceptionBase {
  name: ExceptionName.INVALID_TARIFF_FOR_QUOTE;
}

/**
 * Exception thrown when a application can not be found.
 */
export interface ApplicationNotFoundException extends ExceptionBase {
  name: ExceptionName.APPLICATION_NOT_FOUND;
}

/**
 * Exception thrown when the referenced quote already references an application.
 */
export interface NotAllowedToCreateMultipleApplicationsFromQuoteException
  extends ExceptionBase {
  name: ExceptionName.NOT_ALLOWED_TO_CREATE_MULTIPLE_APPLICATIONS_FROM_QUOTE;
}

/**
 * Exception thrown when the application document can not be found.
 */
export interface ApplicationDocumentNotFoundException extends ExceptionBase {
  name: ExceptionName.APPLICATION_DOCUMENT_NOT_FOUND;
}

/**
 * Exception thrown when the time window for accessing the application has expired.
 */
export interface AccessToApplicationHasExpiredException extends ExceptionBase {
  name: ExceptionName.ACCESS_TO_APPLICATION_HAS_EXPIRED;
}

/**
 * Exception thrown when the document type can not be found.
 */
export interface UnknownApplicationTypeException extends ExceptionBase {
  name: ExceptionName.UNKNOWN_APPLICATION_DOCUMENT_TYPE;
}

/**
 * Exception thrown when the consumer forgot to provide a url for the QR-code which is added as part of the digital application pdf.
 */
export interface ApplicationPdfQrCodeUrlMissingForDigitalApplicationException
  extends ExceptionBase {
  name: ExceptionName.APPLICATION_PDF_QR_CODE_URL_MISSING_FOR_DIGITAL_APPLICATION;
}

/**
 * Union type of all business exceptions which are being exposed by the backend.
 */
export type Exception =
  // General
  | GeneralErrorException
  | NotFoundException
  | ValidationException
  | InvalidUrlException
  // Quote
  | QuoteNotFoundException
  | UnknownTermLifeInsuranceDocumentTypeException
  | InvalidTariffForQuoteException
  // Application
  | ApplicationNotFoundException
  | NotAllowedToCreateMultipleApplicationsFromQuoteException
  | ApplicationDocumentNotFoundException
  | AccessToApplicationHasExpiredException
  | UnknownApplicationTypeException
  | ApplicationPdfQrCodeUrlMissingForDigitalApplicationException
  | CreateApplicationFailedException;
